import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Volume2, VolumeX } from "lucide-react";

const TRACK = "/bg/sak-pase-zo-riddim.mp3";

export default function BackgroundMusic() {
  const audioRef = useRef(null);
  const [enabled, setEnabled] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const heldRef = useRef(false);

  const tryPlay = () => {
    const a = audioRef.current;
    if (!a || !enabled || heldRef.current) return;
    a.play().then(() => { setPlaying(true); setBlocked(false); }).catch(() => setBlocked(true));
  };

  useEffect(() => {
    tryPlay();
    const unlock = () => { tryPlay(); };
    const events = ["pointerdown", "keydown", "touchstart", "wheel"];
    events.forEach((e) => window.addEventListener(e, unlock, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, unlock));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    const onOpen = () => { heldRef.current = true; audioRef.current?.pause(); setPlaying(false); };
    const onClose = () => { heldRef.current = false; tryPlay(); };
    window.addEventListener("zo:media-open", onOpen);
    window.addEventListener("zo:media-close", onClose);
    return () => {
      window.removeEventListener("zo:media-open", onOpen);
      window.removeEventListener("zo:media-close", onClose);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const toggle = () => {
    const a = audioRef.current;
    if (enabled) { a?.pause(); setPlaying(false); setEnabled(false); }
    else { setEnabled(true); }
  };

  return (
    <>
      <audio ref={audioRef} src={TRACK} loop preload="auto" data-testid="background-music-audio" />
      <motion.button
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.8, duration: 0.5 }}
        onClick={toggle}
        data-testid="background-music-toggle"
        aria-label={enabled ? "Mute background music" : "Play background music"}
        className="fixed bottom-6 left-6 z-[60] flex items-center gap-2 bg-black/70 backdrop-blur-md border border-white/15 hover:border-blood px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-gray-300 hover:text-white transition-colors"
      >
        {enabled && playing ? <Volume2 size={14} className="text-blood" /> : <VolumeX size={14} />}
        <span className="hidden sm:inline">{enabled ? (playing ? "Sak Pase Zo Riddim" : blocked ? "Tap for sound" : "Sound on") : "Sound off"}</span>
        {enabled && playing && (
          <span className="flex items-end gap-[2px] h-3" aria-hidden>
            {[0, 1, 2].map((i) => (
              <span key={i} className="w-[2px] bg-blood animate-pulse" style={{ height: `${6 + i * 3}px`, animationDelay: `${i * 0.15}s` }} />
            ))}
          </span>
        )}
      </motion.button>
    </>
  );
}
